import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Lightbulb, Wrench, Search, Heart, Target, Megaphone, Share2, ArrowRight } from 'lucide-react';

const ShareableProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dimensionIcons = {
    B: Lightbulb, U: Wrench, I: Search, L: Heart, D: Target, R: Megaphone
  };

  const dimensionColors = {
    B: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    U: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    I: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    L: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
    D: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
    R: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/share/${userId}`);
      setProfile(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Profile not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Profile Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-indigo-600 hover:bg-indigo-700">
              Discover Your BUILDR Profile
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const PrimaryIcon = dimensionIcons[profile.primary.code];
  const SecondaryIcon = dimensionIcons[profile.secondary.code];
  const primaryColors = dimensionColors[profile.primary.code];
  const secondaryColors = dimensionColors[profile.secondary.code];

  const maxScore = Math.max(...Object.values(profile.scores));

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-indigo-600 mb-4">BUILDR Profile</Badge>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{profile.name}'s Strengths</h1>
          <p className="text-gray-600">Discover what makes them uniquely valuable to any team</p>
        </div>

        {/* Pairing Code Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-center text-white">
            <p className="text-indigo-200 mb-2">BUILDR Pairing Code</p>
            <div className="text-7xl font-black tracking-wider mb-4">{profile.pairing_code}</div>
            <div className="flex justify-center gap-4">
              <Badge className={`${primaryColors.bg} ${primaryColors.text} text-lg px-4 py-1`}>
                {profile.primary.name}
              </Badge>
              <span className="text-white/60">+</span>
              <Badge className={`${secondaryColors.bg} ${secondaryColors.text} text-lg px-4 py-1`}>
                {profile.secondary.name}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Primary & Secondary Dimensions */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className={`border-2 ${primaryColors.border}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${primaryColors.bg}`}>
                  <PrimaryIcon className={`h-6 w-6 ${primaryColors.text}`} />
                </div>
                <div>
                  <Badge variant="outline" className={primaryColors.text}>Primary Strength</Badge>
                  <CardTitle className="text-xl mt-1">{profile.primary.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">{profile.primary.tagline}</p>
            </CardContent>
          </Card>

          <Card className={`border-2 ${secondaryColors.border}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${secondaryColors.bg}`}>
                  <SecondaryIcon className={`h-6 w-6 ${secondaryColors.text}`} />
                </div>
                <div>
                  <Badge variant="outline" className={secondaryColors.text}>Secondary Strength</Badge>
                  <CardTitle className="text-xl mt-1">{profile.secondary.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">{profile.secondary.tagline}</p>
            </CardContent>
          </Card>
        </div>

        {/* Score Breakdown */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Strength Breakdown</CardTitle>
            <CardDescription>How {profile.name} scores across all BUILDR dimensions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(profile.scores)
                .sort((a, b) => b[1] - a[1])
                .map(([dim, score]) => {
                  const Icon = dimensionIcons[dim];
                  const colors = dimensionColors[dim];
                  const pct = (score / maxScore) * 100;
                  const dimensionNames = {
                    B: 'Brainstorm', U: 'Uncover', I: 'Inspect',
                    L: 'Lift', D: 'Deliver', R: 'Rally'
                  };
                  return (
                    <div key={dim} className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.text}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{dimensionNames[dim]}</span>
                          <span className="text-gray-500">{score}</span>
                        </div>
                        <Progress value={pct} className="h-2" />
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-3">Discover Your BUILDR Profile</h2>
            <p className="text-indigo-100 mb-6">
              Find out your unique strengths and how you can contribute to your team, church, or organization.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-white text-indigo-600 hover:bg-indigo-50 text-lg px-8 py-6"
            >
              Take the Assessment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>BUILDR Assessment - Discover Your Strengths</p>
        </div>
      </div>
    </div>
  );
};

export default ShareableProfilePage;
